import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import MarketMap from '../components/MarketMap';

interface MarketData {
	city: string;
	name: string;
	state: string;
	status: 'active' | 'inactive';
	service_radius: number;
	market_map: {
		type: 'Polygon' | 'GeometryCollection';
		coordinates?: number[][][]; // For simple Polygon format
		geometries?: Array<{ // For GeometryCollection format
			type: 'Polygon';
			coordinates: number[][][];
		}>;
	};
	// Additional fields from new API structure
	id?: string;
	date_created?: string;
	date_updated?: string;
	sort?: number | null;
	tesseract_update_requested?: boolean;
	user_created?: string;
}

export default function Resources() {
	const [markets, setMarkets] = useState<MarketData[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		const fetchMarkets = async () => {
			try {
				console.log('Fetching market data from browser...');
				const response = await fetch('https://cortex.ubiquityglass.com/items/markets', {
					method: 'GET',
					mode: 'cors',
					headers: {
						'Accept': 'application/json',
						'Content-Type': 'application/json',
					},
				});
				
				console.log('Response status:', response.status);
				
				if (!response.ok) {
					throw new Error(`HTTP error! status: ${response.status}`);
				}
				
				const data = await response.json();
				console.log('Market data received:', data);
				setMarkets(data.data);
			} catch (err) {
				console.error('Error fetching market data:', err);
				setError(err instanceof Error ? err.message : 'An error occurred');
			} finally {
				setLoading(false);
			}
		};

		fetchMarkets();
	}, []);

	return (
		<div>
			{/* Hero Section */}
			<section className="bg-gradient-to-b from-white to-babyblue/20 border-b gradient-hero">
				<div className="container py-12 md:py-16">
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8 }}
						className="text-center max-w-3xl mx-auto"
					>
						<h1 className="text-3xl md:text-4xl font-bold text-gradient mb-6">Resources & Service Areas</h1>
						<p className="text-lg text-gray-600 mb-8">
							Explore our service coverage, access documentation, and find answers to common questions about Ubiquity AutoGlass services.
						</p>
					</motion.div>
				</div>
			</section>

			{/* Market Map Section */}
			<section className="py-12 md:py-16">
				<div className="container">
					{loading ? (
						<div className="text-center py-12">
							<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
							<p className="text-gray-600">Loading market data...</p>
						</div>
					) : error ? (
						<div className="text-center py-12">
							<div className="text-red-600 mb-4">
								<svg className="w-12 h-12 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
								</svg>
								<p className="text-lg font-semibold">Unable to load market data</p>
								<p className="text-gray-600">{error}</p>
							</div>
						</div>
					) : (
						<MarketMap markets={markets} />
					)}
				</div>
			</section>

			{/* Additional Resources */}
			<section className="py-12 md:py-16 bg-gray-50">
				<div className="container">
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true, amount: 0.3 }}
						transition={{ duration: 0.8 }}
						className="text-center mb-12"
					>
						<h2 className="text-2xl md:text-3xl font-bold text-gradient mb-4">Additional Resources</h2>
						<p className="text-gray-600 max-w-2xl mx-auto">
							Access our comprehensive documentation, API guides, and support resources to integrate with Ubiquity AutoGlass services.
						</p>
					</motion.div>

					<div className="grid md:grid-cols-3 gap-8">
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true, amount: 0.3 }}
							transition={{ duration: 0.8, delay: 0.1 }}
							className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
						>
							<div className="w-12 h-12 bg-babyblue/10 rounded-lg flex items-center justify-center mb-4">
								<svg className="w-6 h-6 text-babyblue" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
								</svg>
							</div>
							<h3 className="text-xl font-semibold mb-2">API Documentation</h3>
							<p className="text-gray-600 mb-4">Comprehensive guides for integrating with our FastLane, FrontLine, and Realm APIs.</p>
							<button className="text-babyblue hover:text-royalpurple font-medium">View Docs →</button>
						</motion.div>

						<motion.div
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true, amount: 0.3 }}
							transition={{ duration: 0.8, delay: 0.2 }}
							className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
						>
							<div className="w-12 h-12 bg-royalpurple/10 rounded-lg flex items-center justify-center mb-4">
								<svg className="w-6 h-6 text-royalpurple" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
								</svg>
							</div>
							<h3 className="text-xl font-semibold mb-2">FAQ</h3>
							<p className="text-gray-600 mb-4">Find answers to frequently asked questions about our services and technology.</p>
							<button className="text-babyblue hover:text-royalpurple font-medium">View FAQ →</button>
						</motion.div>

						<motion.div
							initial={{ opacity: 0, y: 20 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true, amount: 0.3 }}
							transition={{ duration: 0.8, delay: 0.3 }}
							className="bg-white rounded-xl p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow"
						>
							<div className="w-12 h-12 bg-orangepeel/10 rounded-lg flex items-center justify-center mb-4">
								<svg className="w-6 h-6 text-orangepeel" fill="none" stroke="currentColor" viewBox="0 0 24 24">
									<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M12 2.25a9.75 9.75 0 100 19.5 9.75 9.75 0 000-19.5z" />
								</svg>
							</div>
							<h3 className="text-xl font-semibold mb-2">Integration Guides</h3>
							<p className="text-gray-600 mb-4">Step-by-step guides for integrating Ubiquity AutoGlass into your workflow.</p>
							<button className="text-babyblue hover:text-royalpurple font-medium">View Guides →</button>
						</motion.div>
					</div>
				</div>
			</section>

			{/* CTA Section */}
			<section className="animated-gradient particle-container subtle-glow">
				<div className="container py-12 md:py-16 text-white text-center">
					<motion.h3
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true, amount: 0.3 }}
						transition={{ duration: 0.8 }}
						className="text-2xl font-bold mb-4"
					>
						Ready to Get Started?
					</motion.h3>
					<motion.p
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true, amount: 0.3 }}
						transition={{ duration: 0.8, delay: 0.1 }}
						className="mb-6 max-w-2xl mx-auto"
					>
						Explore our service areas and start integrating with Ubiquity AutoGlass today.
					</motion.p>
					<motion.div
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true, amount: 0.3 }}
						transition={{ duration: 0.8, delay: 0.2 }}
						className="flex gap-3 justify-center"
					>
						<a href="https://getstarted.ubiquityglass.com" target="_blank" rel="noreferrer">
							<button className="bg-white text-secondary hover:opacity-90 px-6 py-3 rounded-md font-semibold">
								Get Started
							</button>
						</a>
						<a href="/support">
							<button className="border border-white text-white hover:bg-white/10 bg-transparent px-6 py-3 rounded-md font-semibold">
								Contact Sales
							</button>
						</a>
					</motion.div>
				</div>
			</section>
		</div>
	);
}
